<template>
  <Layout>
    <div class="form-builder">
      <!-- Header -->
      <div class="builder-header">
        <div class="header-left">
          <button class="btn-back" @click="goBack">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="form-title-section">
            <input
              v-model="formData.name"
              type="text"
              class="form-title-input"
              placeholder="Nome do formulário"
            />
            <span class="form-status" :class="formData.status === 1 ? 'active' : 'inactive'">
              {{ formData.status === 1 ? 'Ativo' : 'Inativo' }}
            </span>
          </div>
        </div>
        <div class="header-right">
          <button class="btn-settings" @click="showSettingsModal = true">
            <i class="bx bx-cog"></i>
            Configurações
          </button>
          <button class="btn-preview" @click="previewForm">
            <i class="bx bx-show"></i>
            Visualizar
          </button>
          <button class="btn-save" @click="saveForm" :disabled="saving">
            <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <!-- Steps Bar (oculto no formulário de coleta de lead — é sempre flat) -->
      <div class="steps-bar" v-if="!isLeadForm">
        <div class="steps-container">
          <div
            class="step-tab"
            :class="{ 'active': !selectedStep }"
            @click="selectStep(null)"
          >
            <span class="step-icon-all"><i class="bx bx-list-ul"></i></span>
            <span class="step-name">Todos</span>
            <span class="step-count">{{ fieldsWithoutStep }}</span>
          </div>

          <draggable
            v-model="localSteps"
            handle=".step-drag-handle"
            animation="200"
            class="steps-draggable"
            @end="onStepsReorder"
          >
            <div
              v-for="(step, index) in localSteps"
              :key="step.id ? `id-${step.id}` : `tmp-${step._tempId}`"
              class="step-tab"
              :class="{
                'active': isStepSelected(step),
                'companion-step': step.is_companion_step || (step.name || '').startsWith('Acompanhante')
              }"
              @click="selectStep(step)"
            >
              <span class="step-drag-handle">
                <i class="bx bx-grid-vertical"></i>
              </span>
              <span class="step-number">{{ index + 1 }}</span>
              <span class="step-name">
                <i v-if="step.is_companion_step || (step.name || '').startsWith('Acompanhante')" class="bx bx-lock-alt text-info mr-1" title="Step de acompanhante (não editável)"></i>
                {{ step.name }}
              </span>
              <span class="step-count">{{ getStepFieldCount(step) }}</span>
              <button
                class="step-edit-btn"
                @click.stop="editStep(step)"
                :style="(step.is_companion_step || (step.name || '').startsWith('Acompanhante')) ? 'opacity: 0.3; cursor: not-allowed;' : ''"
              >
                <i class="bx bx-pencil"></i>
              </button>
              <button
                class="step-delete-btn"
                @click.stop="deleteStep(step)"
                :style="(step.is_companion_step || (step.name || '').startsWith('Acompanhante')) ? 'opacity: 0.3; cursor: not-allowed;' : ''"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>
          </draggable>

          <button class="btn-add-step" @click="addStep">
            <i class="bx bx-plus"></i>
            Nova Etapa
          </button>
        </div>
      </div>

      <!-- Loading Overlay -->
      <transition name="fade-overlay">
        <div v-if="pageLoading" class="builder-loading-overlay">
          <div class="builder-loading-content">
            <div class="builder-loading-spinner"></div>
            <p>Carregando formulário...</p>
          </div>
        </div>
      </transition>

      <!-- Main Content -->
      <div class="builder-content">
        <!-- Left Panel: Field Palette -->
        <div class="panel panel-left">
          <FieldPalette
            :field-types-grouped="fieldTypesGrouped"
            :loading="loadingTypes"
            :existing-fields="localFields"
            :steps="localSteps"
            :selected-step="selectedStep"
            :settings="rsvpSettings || {}"
            :custom-only="isLeadForm"
            @add-field="addCustomField"
            @add-library-field="addLibraryField"
            @add-field-group="addFieldGroup"
            @add-rc-field="addRegistrationControlField"
          />
        </div>

        <!-- Center Panel: Form Canvas -->
        <div class="panel panel-center">
          <div class="canvas-header">
            <h5 v-if="selectedStep">
              <i class="bx bx-layer"></i>
              {{ selectedStep.name }}
            </h5>
            <h5 v-else>
              <i class="bx bx-list-ul"></i>
              Todos os Campos
            </h5>
            <span class="field-count">{{ filteredFields.length }} campos</span>
          </div>

          <div class="canvas-scroll">
            <FormCanvas
              :fields="filteredFields"
              :selected-field="selectedField"
              @select-field="handleSelectField"
              @update-field="handleUpdateField"
              @remove-field="handleRemoveField"
              @duplicate-field="handleDuplicateField"
              @reorder-fields="handleReorderFields"
              @add-field="handleCanvasDrop"
            />
          </div>
        </div>

        <!-- Right Panel: Field Editor -->
        <div class="panel panel-right" :class="{ 'has-selection': selectedField }">
          <div v-if="!selectedField" class="no-selection">
            <div class="no-selection-icon">
              <i class="bx bx-pointer"></i>
            </div>
            <h5>Nenhum campo selecionado</h5>
            <p>Clique em um campo para editar suas propriedades</p>
          </div>

          <FieldEditor
            v-else
            :field="selectedField"
            :all-fields="localFields"
            :steps="localSteps"
            :registration-type="rsvpSettings ? rsvpSettings.registration_type : null"
            :attachment-types="attachmentTypes"
            @update-field="handleUpdateField"
            @close="clearSelection"
          />
        </div>
      </div>

      <!-- Settings Modal -->
      <b-modal
        v-model="showSettingsModal"
        title="Configurações do Formulário"
        size="lg"
        ok-title="Salvar"
        cancel-title="Cancelar"
        @ok="saveSettings"
        centered
      >
        <div class="settings-form">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Nome do Formulário *</label>
                <input
                  v-model="formData.name"
                  type="text"
                  class="form-control"
                  placeholder="Digite o nome"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Slug (URL)</label>
                <input
                  v-model="formData.slug"
                  type="text"
                  class="form-control"
                  placeholder="meu-formulario"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Status</label>
                <select v-model.number="formData.status" class="form-control">
                  <option :value="1">Ativo</option>
                  <option :value="0">Inativo</option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Descrição</label>
            <textarea
              v-model="formData.description"
              class="form-control"
              rows="2"
              placeholder="Descrição do formulário (opcional)"
            ></textarea>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Grupo</label>
                <groups-select
                  :value="formData.group_id"
                  @input="setGroup"
                  placeholder="Selecione o grupo..."
                />
                <small class="text-muted">
                  Vincular formulário a um grupo específico
                </small>
              </div>
            </div>
            <div class="col-md-6" v-if="!isLeadForm">
              <div class="form-group">
                <label>Etapa de revisão</label>
                <select v-model="formSettings.has_review_step" class="form-control">
                  <option :value="true">Sim — exibir revisão antes do envio</option>
                  <option :value="false">Não — enviar direto sem revisão</option>
                </select>
                <small class="text-muted">
                  Exibir etapa para o participante revisar os dados antes de enviar
                </small>
              </div>
            </div>
          </div>

          <!-- Acompanhantes não se aplicam ao formulário de coleta de lead -->
          <template v-if="!isLeadForm">
          <hr />

          <!-- Configurações de Acompanhantes -->
          <h6><i class="bx bx-group mr-2"></i>Configurações de Acompanhantes</h6>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Habilitar Acompanhantes</label>
                <select v-model="companionConfig.enabled" class="form-control">
                  <option :value="false">Não</option>
                  <option :value="true">Sim</option>
                </select>
                <small class="form-text text-muted">
                  Permite que participantes cadastrem acompanhantes
                </small>
              </div>
            </div>

            <div class="col-md-6" v-if="companionConfig.enabled">
              <div class="form-group">
                <label>Modo de Cadastro</label>
                <select v-model="companionConfig.mode" class="form-control">
                  <option value="invitation">Indicar (acompanhante preenche depois)</option>
                  <option value="direct">Preencher dados (tudo de uma vez)</option>
                </select>
                <small class="form-text text-muted">
                  <strong>Indicar:</strong> Participante fornece dados básicos, acompanhante preenche formulário depois<br>
                  <strong>Preencher:</strong> Participante preenche todos os dados do acompanhante
                </small>
              </div>
            </div>
          </div>

          <div class="row" v-if="companionConfig.enabled">
            <div class="col-md-6">
              <div class="form-group">
                <label>Quantidade Máxima de Acompanhantes</label>
                <select v-model.number="companionConfig.max_companions" class="form-control">
                  <option :value="1">1 acompanhante</option>
                  <option :value="2">2 acompanhantes</option>
                  <option :value="3">3 acompanhantes</option>
                  <option :value="4">4 acompanhantes</option>
                  <option :value="5">5 acompanhantes</option>
                </select>
              </div>
            </div>

            <div class="col-md-6" v-if="companionConfig.mode === 'invitation'">
              <div class="form-group">
                <label>Formulário para o Acompanhante</label>
                <select v-model.number="companionConfig.companion_form_id" class="form-control">
                  <option :value="null">Usar o mesmo formulário</option>
                  <option v-for="f in availableForms" :key="f.id" :value="f.id">
                    {{ f.name }}
                  </option>
                </select>
                <small class="form-text text-muted">
                  Qual formulário o acompanhante preencherá ao clicar no link de convite
                </small>
              </div>
            </div>
          </div>

          <div class="row" v-if="companionConfig.enabled && companionConfig.mode === 'invitation'">
            <div class="col-md-6">
              <div class="form-group">
                <label>Template de E-mail de Convite <span class="text-danger">*</span></label>
                <select v-model.number="companionConfig.email_template_id" class="form-control" required>
                  <option :value="null" disabled>Selecione um template</option>
                  <option v-for="template in emailTemplates" :key="template.id" :value="template.id">
                    {{ template.type_email_label || 'Template Personalizado' }} - {{ template.subject }}
                  </option>
                </select>
                <small class="form-text text-muted">
                  Template de e-mail que será enviado para o acompanhante com o link de convite
                  <a href="/settings/emails" target="_blank" class="ml-2">
                    <i class="mdi mdi-cog"></i> Gerenciar templates
                  </a>
                </small>
              </div>
            </div>
          </div>

          <hr />

          <h6><i class="mdi mdi-check-circle mr-2"></i>Página de Obrigado</h6>
          <div class="form-group">
            <label>Selecione a Página</label>
            <select v-model="formData.thank_you_page_id" class="form-control">
              <option :value="null">Usar página padrão do sistema</option>
              <option v-for="page in thankYouPages" :key="page.id" :value="page.id">
                {{ page.name }}
                <span v-if="page.is_default"> (Padrão)</span>
              </option>
            </select>
            <small class="text-muted">
              Escolha qual página de obrigado exibir após o envio do formulário.
              <a href="#" @click.prevent="openThankYouPagesManager" class="ml-2">
                <i class="mdi mdi-cog"></i> Gerenciar páginas
              </a>
            </small>
          </div>
          </template>
        </div>
      </b-modal>

      <!-- Step Modal -->
      <b-modal
        v-model="showStepModal"
        :title="editingStep && (editingStep.id || editingStep._tempId) ? 'Editar Etapa' : 'Nova Etapa'"
        ok-title="Salvar"
        cancel-title="Cancelar"
        @ok="saveStep"
        centered
      >
        <div class="form-group">
          <label>Nome da Etapa *</label>
          <input
            v-model="editingStep.name"
            type="text"
            class="form-control"
            placeholder="Ex: Dados Pessoais"
          />
        </div>
        <div class="form-group mb-0">
          <label>Descrição</label>
          <textarea
            v-model="editingStep.description"
            class="form-control"
            rows="2"
            placeholder="Descrição opcional..."
          ></textarea>
        </div>
      </b-modal>

      <!-- Preview Modal -->
      <b-modal
        v-model="showPreview"
        title="Preview do Formulário"
        size="xl"
        hide-footer
        centered
        body-class="preview-modal-body"
      >
        <div class="form-preview">
          <div class="preview-header">
            <h4>{{ formData.name }}</h4>
            <p v-if="formData.description">{{ formData.description }}</p>
          </div>

          <!-- Steps Navigation (if has steps) -->
          <div v-if="localSteps.length > 0" class="preview-steps">
            <div class="preview-steps-bar">
              <div
                v-for="(step, index) in localSteps"
                :key="step.id || step._tempId"
                class="preview-step-item"
                :class="{ 'active': previewCurrentStep === index, 'completed': previewCurrentStep > index }"
                @click="previewCurrentStep = index"
              >
                <span class="preview-step-number">{{ index + 1 }}</span>
                <span class="preview-step-name">{{ step.name }}</span>
              </div>
            </div>

            <div class="preview-step-content">
              <h5 class="preview-step-title">
                {{ getCurrentPreviewStep ? getCurrentPreviewStep.name : '' }}
              </h5>
              <p v-if="getCurrentPreviewStep && getCurrentPreviewStep.description" class="preview-step-desc">
                {{ getCurrentPreviewStep.description }}
              </p>

              <div class="preview-fields-grid">
                <div
                  v-for="field in getPreviewStepFields(previewCurrentStep)"
                  :key="field.id || field._tempId"
                  class="preview-field"
                  :class="getFieldWidthClass(field)"
                >
                  <label>
                    {{ field.label }}
                    <span v-if="field.required" class="required-mark">*</span>
                  </label>
                  <component
                    :is="getPreviewFieldComponent(field)"
                    :field="field"
                  />
                  <small v-if="field.help_text" class="help-text">{{ field.help_text }}</small>
                </div>
              </div>

              <!-- Step Navigation -->
              <div class="preview-step-nav">
                <button
                  v-if="previewCurrentStep > 0"
                  class="btn btn-secondary"
                  @click="previewCurrentStep--"
                >
                  <i class="bx bx-chevron-left"></i> Voltar
                </button>
                <button
                  v-if="previewCurrentStep < localSteps.length - 1"
                  class="btn btn-primary"
                  @click="previewCurrentStep++"
                >
                  Próximo <i class="bx bx-chevron-right"></i>
                </button>
                <button
                  v-else
                  class="btn btn-success"
                  disabled
                >
                  {{ formSettings.submit_text || 'Enviar' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Single Page (no steps) -->
          <div v-else class="preview-single-page">
            <div class="preview-fields-grid">
              <div
                v-for="field in localFields"
                :key="field.id || field._tempId"
                class="preview-field"
                :class="getFieldWidthClass(field)"
              >
                <label>
                  {{ field.label }}
                  <span v-if="field.required" class="required-mark">*</span>
                </label>
                <component
                  :is="getPreviewFieldComponent(field)"
                  :field="field"
                />
                <small v-if="field.help_text" class="help-text">{{ field.help_text }}</small>
              </div>
            </div>

            <div v-if="localFields.length === 0" class="empty-preview">
              <i class="bx bx-layer"></i>
              <p>Nenhum campo adicionado ainda</p>
            </div>

            <div v-else class="preview-submit">
              <button class="btn btn-success" disabled>
                {{ formSettings.submit_text || 'Enviar' }}
              </button>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import draggable from 'vuedraggable'
import GroupsSelect from '@/components/selects/groups-select'
import FieldPalette from './components/FieldPalette.vue'
import FormCanvas from './components/FormCanvas.vue'
import FieldEditor from './components/FieldEditor.vue'
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import thankYouPagesService from './thank-you-pages/services'
import http from '@/http'

export default {
  name: 'FormBuilder',
  components: {
    Layout,
    draggable,
    GroupsSelect,
    FieldPalette,
    FormCanvas,
    FieldEditor
  },
  data () {
    return {
      formData: {
        id: null,
        name: '',
        type: 'registration',
        slug: '',
        description: '',
        status: 1,
        group_id: null,
        link_companion: 0,
        form_companion_id: null,
        success_screen_title: null,
        success_screen_message: null,
        success_screen_icon: null,
        thank_you_page_id: null,
        companion_config: null
      },
      companionConfig: {
        enabled: false,
        mode: 'invitation',
        max_companions: 1,
        companion_form_id: null,
        email_template_id: null
      },
      thankYouPages: [],
      emailTemplates: [],
      attachmentTypes: [],
      formSettings: {
        submit_text: 'Enviar',
        redirect_url: '',
        success_message: 'Formulário enviado com sucesso!',
        show_progress: true,
        has_review_step: true
      },
      localFields: [],
      localSteps: [],
      selectedField: null,
      selectedStep: null,
      pageLoading: true,
      loadingTypes: false,
      saving: false,
      showSettingsModal: false,
      showStepModal: false,
      showPreview: false,
      previewCurrentStep: 0,
      availableForms: [],
      editingStep: {
        name: '',
        description: ''
      },
      tempIdCounter: 1
    }
  },
  computed: {
    ...mapState('formBuilder', ['fieldTypesGrouped', 'fields']),
    ...mapState('settingRSVP', { rsvpSettings: 'settingRSVP' }),
    isEditing () {
      return !!this.$route.params.id
    },
    // Formulário de coleta de lead — detectado pela rota (instantâneo) ou pelo type
    isLeadForm () {
      const path = (this.$route && this.$route.path) || ''
      return this.formData.type === 'checkin_lead' || path.indexOf('/settings/checkin/lead-form') === 0
    },
    isCompanionEnabled () {
      // Verifica se tipo de inscrição é "Com acompanhante" (valor 2)
      return this.rsvpSettings && parseInt(this.rsvpSettings.registration_type) === 2
    },
    filteredFields () {
      if (!this.selectedStep) {
        // Quando "Todos" está selecionado, mostrar apenas campos SEM step
        return this.localFields.filter(f => f.step_id === null || f.step_id === undefined)
      }
      // Filtrar por step_id correto (converter para string para comparação)
      const stepId = this.selectedStep.id
      const stepTempId = this.selectedStep._tempId

      return this.localFields.filter(f => {
        const matchById = stepId && String(f.step_id) === String(stepId)
        const matchByTempId = stepTempId && String(f.step_id) === String(stepTempId)
        return matchById || matchByTempId
      })
    },
    fieldsWithoutStep () {
      // Contar apenas campos SEM step (step_id = null ou undefined)
      return this.localFields.filter(f => f.step_id === null || f.step_id === undefined).length
    },
    getCurrentPreviewStep () {
      return this.localSteps[this.previewCurrentStep] || null
    }
  },
  async mounted () {
    // Hide footer on this page
    document.body.classList.add('form-builder-page')

    this.pageLoading = true
    try {
      await this.loadFieldTypes()

      // Carregar configurações RSVP para verificar tipo de inscrição
      await this.loadRSVPSettings()

      // Carregar páginas de obrigado disponíveis
      await this.loadThankYouPages()

      // Carregar templates de e-mail disponíveis
      await this.loadEmailTemplates()

      // Carregar tipos de anexo cadastrados (para campos de upload)
      await this.loadAttachmentTypes()

      if (this.isEditing) {
        await this.loadForm(this.$route.params.id)
      } else {
        // Definir tipo do formulário a partir do query param
        const formType = this.$route.query.type || 'registration'
        this.formData.type = formType

        // Novo formulário: auto-adicionar campos standard (apenas para inscrição)
        if (formType !== 'action') {
          await this.initializeStandardFields()
        }
      }

      this.loadAvailableForms()

      // Se tem steps, seleciona o primeiro automaticamente
      if (this.localSteps.length > 0) {
        this.selectedStep = this.localSteps[0]
      }
    } finally {
      this.pageLoading = false
    }
  },
  beforeDestroy () {
    // Restore footer when leaving this page
    document.body.classList.remove('form-builder-page')
    // Limpar o store ao sair
    this.$store.commit('formBuilder/RESET_BUILDER')
  },
  methods: {
    ...mapActions('formBuilder', [
      'GetFormBuilder',
      'CreateForm',
      'SaveFormComplete',
      'GetFieldTypesGrouped',
      'GetForms',
      'InitializeStandardFields'
    ]),
    ...mapActions('settingRSVP', { GetRSVP: 'GetRSVP' }),

    generateTempId () {
      return `temp_${Date.now()}_${this.tempIdCounter++}`
    },

    async loadFieldTypes () {
      this.loadingTypes = true
      try {
        await this.GetFieldTypesGrouped()
      } catch (err) {
        console.error('Error loading field types:', err)
      } finally {
        this.loadingTypes = false
      }
    },

    async loadRSVPSettings () {
      try {
        await this.GetRSVP()
      } catch (err) {
        console.error('Error loading RSVP settings:', err)
      }
    },

    async loadThankYouPages () {
      try {
        const response = await thankYouPagesService.listActive()
        this.thankYouPages = response.data
      } catch (err) {
        console.error('Error loading thank you pages:', err)
      }
    },

    async loadEmailTemplates () {
      try {
        const response = await http.get('/admin/settings/email')
        this.emailTemplates = response.data.data || response.data || []
      } catch (err) {
        console.error('Error loading email templates:', err)
        this.emailTemplates = []
      }
    },

    async loadAttachmentTypes () {
      try {
        // /admin/settings/attachment-type — endpoint dedicado a dropdowns,
        // já filtra active=true no server e retorna { id: name, ... }
        const response = await http.get('/admin/settings/attachment-type')
        const map = response.data || {}
        if (Array.isArray(map)) {
          // Compat: se vier como array de objetos
          this.attachmentTypes = map
            .filter(function (t) { return t && (t.active === undefined || t.active) })
            .map(function (t) { return { id: t.id, name: t.name } })
        } else {
          this.attachmentTypes = Object.keys(map).map(function (id) {
            return { id: Number(id), name: map[id] }
          })
        }
      } catch (err) {
        console.error('Error loading attachment types:', err)
        this.attachmentTypes = []
      }
    },

    openThankYouPagesManager () {
      this.$router.push({ name: 'form-builder-thank-you-pages' })
    },

    async initializeStandardFields () {
      try {
        console.log('🚀 Iniciando inicialização de campos standard...')

        // Limpar o store antes de inicializar campos standard
        this.$store.commit('formBuilder/RESET_BUILDER')

        const count = await this.InitializeStandardFields()
        console.log('🔄 Sincronizando campos do store para localFields...')
        console.log('📦 Store fields:', this.fields.length)
        // Sincronizar os campos do store com localFields
        this.localFields = [...this.fields]
        console.log('📦 LocalFields após sincronização:', this.localFields.length)
        console.log(`✅ ${count} campos standard inicializados automaticamente`)
      } catch (err) {
        console.error('❌ Error initializing standard fields:', err)
      }
    },

    async loadForm (id) {
      try {
        // Limpar o store antes de carregar para evitar duplicação
        this.$store.commit('formBuilder/RESET_BUILDER')
        const data = await this.GetFormBuilder(id)
        if (data) {
          this.formData = {
            id: data.id,
            name: data.name || '',
            type: data.type || 'registration',
            slug: data.slug || '',
            description: data.description || '',
            status: data.status ? 1 : 0,
            group_id: data.group_id,
            link_companion: data.link_companion ? 1 : 0,
            form_companion_id: data.form_companion_id,
            thank_you_page_id: data.thank_you_page_id,
            companion_config: data.companion_config
          }
          this.formSettings = data.settings || this.formSettings

          // Carrega companion_config
          if (data.companion_config && data.companion_config.enabled) {
            this.companionConfig = {
              enabled: data.companion_config.enabled || false,
              mode: data.companion_config.mode || 'invitation',
              max_companions: data.companion_config.max_companions || 1,
              companion_form_id: data.companion_config.companion_form_id || null,
              email_template_id: data.companion_config.email_template_id || null
            }
          }

          // DEBUG: Log dos dados brutos
          console.log('🔍 DEBUG - data.fields:', data.fields?.length || 0)
          console.log('🔍 DEBUG - data.steps:', data.steps?.length || 0)
          if (data.fields && data.fields.length > 0) {
            console.log('📋 Campos em data.fields:', data.fields.map(f => `${f.name} (ID: ${f.id})`))
          }
          if (data.steps && data.steps.length > 0) {
            data.steps.forEach((step, idx) => {
              if (step.fields && step.fields.length > 0) {
                console.log(`📋 Campos em step[${idx}] "${step.name}":`, step.fields.map(f => `${f.name} (ID: ${f.id})`))
              }
            })
          }

          // Combinar campos: fields sem step_id + campos dentro dos steps
          const fieldsWithoutStep = (data.fields || []).map(f => ({
            ...f,
            fieldType: f.fieldType || f.field_type || null,
            _tempId: f.id ? null : this.generateTempId()
          }))

          const fieldsFromSteps = []
          if (data.steps && Array.isArray(data.steps)) {
            data.steps.forEach(step => {
              if (step.fields && Array.isArray(step.fields)) {
                step.fields.forEach(f => {
                  // Garante que step_id está definido a partir do step pai
                  fieldsFromSteps.push({
                    ...f,
                    fieldType: f.fieldType || f.field_type || null,
                    step_id: f.step_id || step.id, // Usa step.id como fallback
                    _tempId: f.id ? null : this.generateTempId()
                  })
                })
              }
            })
          }

          // Combinar todos os campos, removendo duplicatas pelo ID
          const allFields = [...fieldsWithoutStep, ...fieldsFromSteps]
          const uniqueFieldsMap = new Map()
          allFields.forEach(field => {
            if (field.id) {
              // Se o campo tem ID, usar ID como chave para evitar duplicatas
              if (!uniqueFieldsMap.has(field.id)) {
                uniqueFieldsMap.set(field.id, field)
              }
            } else {
              // Se não tem ID, adicionar diretamente (é um campo novo com _tempId)
              const tempKey = field._tempId || `temp_${Date.now()}_${Math.random()}`
              uniqueFieldsMap.set(tempKey, field)
            }
          })

          this.localFields = Array.from(uniqueFieldsMap.values())
          console.log(`📦 Carregados ${this.localFields.length} campos únicos (${allFields.length} total antes da deduplicação)`)

          // Carregar steps (sem os campos aninhados para evitar duplicação)
          this.localSteps = (data.steps || []).map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            icon: s.icon,
            order: s.order,
            conditions: s.conditions,
            settings: s.settings,
            form_id: s.form_id,
            is_companion_step: s.is_companion_step || false,
            companion_index: s.companion_index || null,
            _tempId: s.id ? null : this.generateTempId()
          }))

          // Só cria steps de acompanhante se o companion está habilitado
          // mas nenhum step de acompanhante existe no banco ainda
          const hasCompanionStepsFromDB = this.localSteps.some(s => s.is_companion_step && s.id)
          if (this.companionConfig.enabled && !hasCompanionStepsFromDB) {
            this.syncCompanionSteps()
          }
        }
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao carregar formulário')
        this.$router.push(this.$route.path.indexOf('/settings/checkin') === 0 ? '/settings/checkin' : { name: 'form-builder' })
      }
    },

    async loadAvailableForms () {
      try {
        const res = await this.GetForms({ limit: 100 })
        this.availableForms = (res.data || []).filter(f => f.id !== this.formData.id)
      } catch (err) {
        console.error(err)
      }
    },

    setGroup (groupId) {
      this.formData.group_id = groupId
    },

    // Field Management
    addLibraryField (libraryField) {
      const newField = {
        ...JSON.parse(JSON.stringify(libraryField)),
        id: null,
        _tempId: this.generateTempId(),
        order: this.localFields.length,
        step_id: this.selectedStep ? (this.selectedStep.id || this.selectedStep._tempId) : null,
        // Garantir que field_type_id está presente (resolve pelo nome se necessário)
        field_type_id: libraryField.field_type_id || libraryField.fieldType?.id || (libraryField.fieldType?.name ? this.getFieldTypeIdByName(libraryField.fieldType.name) : undefined)
      }
      // Remove library id prefix
      if (newField.id && String(newField.id).startsWith('lib_')) {
        delete newField.id
      }

      // Garantir nome único (campos da biblioteca podem vir com nome repetido)
      if (!newField.name) {
        newField.name = this.generateFieldName(newField.label || 'campo')
      } else {
        // Se já existe campo com mesmo nome, gerar nome único
        var nameExists = this.localFields.some(function (f) { return f.name === newField.name })
        if (nameExists) {
          newField.name = this.generateFieldName(newField.name)
        }
      }

      this.localFields.push(newField)
      this.selectedField = newField
    },

    addFieldGroup (group) {
      if (!group || !group.fields || group.fields.length === 0) return

      const addedFields = []
      group.fields.forEach((field) => {
        const fieldType = field.fieldType || null
        const newField = {
          _tempId: this.generateTempId(),
          label: field.label,
          name: this.generateFieldName(field.name || field.label || 'campo'),
          field_type_id: field.field_type_id || fieldType?.id || (fieldType?.name ? this.getFieldTypeIdByName(fieldType.name) : undefined),
          fieldType: fieldType,
          placeholder: field.placeholder || '',
          help_text: field.help_text || '',
          required: field.required || false,
          order: this.localFields.length + addedFields.length,
          step_id: this.selectedStep ? (this.selectedStep.id || this.selectedStep._tempId) : null,
          options: field.options || null,
          config: field.config || {},
          layout: field.layout || { cols: '12' },
          behaviors: fieldType?.behaviors || [],
          conditions: [],
          validations: []
        }
        addedFields.push(newField)
      })

      this.localFields.push(...addedFields)
      if (addedFields.length > 0) {
        this.selectedField = addedFields[0]
      }
    },

    addRegistrationControlField (rcPaletteItem) {
      // O field_type_id vem resolvido da API junto do campo do controle. O
      // getFieldTypeIdByName cobre payloads antigos, sem o ID.
      const typeName = rcPaletteItem.rc_type || 'text'
      const fieldTypeId = rcPaletteItem.rc_field_type_id || this.getFieldTypeIdByName(typeName)

      const newField = {
        _tempId: this.generateTempId(),
        label: rcPaletteItem.label,
        name: 'rc_' + rcPaletteItem.rc_field,
        field_type_id: fieldTypeId,
        fieldType: { name: typeName },
        placeholder: '',
        help_text: '',
        required: false,
        disabled: true,
        readonly: true,
        order: this.localFields.length,
        step_id: this.selectedStep ? (this.selectedStep.id || this.selectedStep._tempId) : null,
        options: rcPaletteItem.rc_options || null,
        config: {
          source: 'registration_control',
          rc_field: rcPaletteItem.rc_field
        },
        layout: { cols: '6' },
        conditions: [],
        validations: []
      }

      // Apply mask if present
      if (rcPaletteItem.rc_mask) {
        newField.mask = rcPaletteItem.rc_mask
      }

      this.localFields.push(newField)
      this.selectedField = newField
    },

    addCustomField (fieldData) {
      // fieldData can come pre-configured from the FieldPalette custom form.
      // O FieldPalette emite `format`/`formatLabel` em vez de `fieldType.{name,label}`,
      // então normalizamos para garantir fieldType.name populado — chave usada
      // por isTextLikeType/isDateType/isFileType no FieldEditor e pelos mappers
      // de validação/render no lp e widget.
      const fieldType = fieldData.fieldType || {
        id: fieldData.field_type_id,
        name: fieldData.format,
        label: fieldData.formatLabel || fieldData.label,
        icon: fieldData.icon,
        component: fieldData.component,
        mask: fieldData.mask
      }
      const newField = {
        _tempId: this.generateTempId(),
        label: fieldData.label || fieldType.label || 'Novo Campo',
        name: this.generateFieldName(fieldData.label || fieldType.label || 'campo'),
        fieldType: fieldType,
        field_type_id: fieldData.field_type_id || fieldType.id,
        placeholder: fieldData.placeholder || '',
        help_text: fieldData.help_text || '',
        required: fieldData.required || false,
        order: this.localFields.length,
        step_id: this.selectedStep ? (this.selectedStep.id || this.selectedStep._tempId) : null,
        options: fieldData.options || (['select', 'multiselect', 'radio', 'checkbox'].includes(fieldType.name)
          ? [{ label: 'Opção 1', value: 'option_1' }]
          : null),
        conditions: [],
        validations: fieldData.validations || [],
        config: fieldData.config || {}
      }
      this.localFields.push(newField)
      this.selectedField = newField
    },

    handleCanvasDrop (dropData) {
      try {
        const parsed = typeof dropData === 'string' ? JSON.parse(dropData) : dropData
        const item = parsed.item || parsed
        if (item && item.is_rc_field) {
          this.addRegistrationControlField(item)
        } else if (parsed.source === 'library') {
          this.addLibraryField(item)
        } else {
          this.addCustomField(item)
        }
      } catch (e) {
        // Legacy format - just a field type
        this.addCustomField(dropData)
      }
    },

    generateFieldName (label) {
      const base = label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')

      const existing = this.localFields.filter(f => f.name && f.name.startsWith(base))
      if (existing.length === 0) {
        return base
      }
      return `${base}_${existing.length + 1}`
    },

    handleSelectField (field) {
      this.selectedField = field
    },

    handleUpdateField (updatedField) {
      const index = this.localFields.findIndex(f =>
        (f.id && f.id === updatedField.id) ||
        (f._tempId && f._tempId === updatedField._tempId)
      )
      if (index !== -1) {
        this.$set(this.localFields, index, { ...updatedField })
        this.selectedField = this.localFields[index]
      }
    },

    handleRemoveField (fieldId) {
      const index = this.localFields.findIndex(f => f.id === fieldId || f._tempId === fieldId)
      if (index !== -1) {
        this.localFields.splice(index, 1)
        if (this.selectedField && (this.selectedField.id === fieldId || this.selectedField._tempId === fieldId)) {
          this.selectedField = null
        }
      }
    },

    handleDuplicateField (fieldId) {
      const field = this.localFields.find(f => f.id === fieldId || f._tempId === fieldId)
      if (field) {
        const newField = {
          ...JSON.parse(JSON.stringify(field)),
          id: null,
          _tempId: this.generateTempId(),
          name: this.generateFieldName(field.label + '_copy'),
          label: field.label + ' (cópia)',
          order: this.localFields.length
        }
        this.localFields.push(newField)
        this.selectedField = newField
      }
    },

    handleReorderFields (reorderedFields) {
      const updatedFields = reorderedFields.map((field, index) => ({
        ...field,
        order: index
      }))

      if (this.selectedStep) {
        const otherFields = this.localFields.filter(f =>
          String(f.step_id) !== String(this.selectedStep.id) &&
          String(f.step_id) !== String(this.selectedStep._tempId)
        )
        this.localFields = [...otherFields, ...updatedFields]
      } else {
        this.localFields = updatedFields
      }
    },

    clearSelection () {
      this.selectedField = null
    },

    // Step Management
    selectStep (step) {
      this.selectedStep = step
      this.selectedField = null
    },

    isStepSelected (step) {
      if (!this.selectedStep) return false
      // Comparar por id se ambos tiverem id real (converter para string)
      if (this.selectedStep.id && step.id) {
        return String(this.selectedStep.id) === String(step.id)
      }
      // Comparar por _tempId se algum for novo (sem id real)
      if (this.selectedStep._tempId && step._tempId) {
        return String(this.selectedStep._tempId) === String(step._tempId)
      }
      return false
    },

    getStepFieldCount (step) {
      return this.localFields.filter(f => {
        if (f.step_id === null || f.step_id === undefined) return false
        const matchId = step.id && String(f.step_id) === String(step.id)
        const matchTempId = step._tempId && String(f.step_id) === String(step._tempId)
        return matchId || matchTempId
      }).length
    },

    addStep () {
      this.editingStep = {
        name: '',
        description: ''
      }
      this.showStepModal = true
    },

    editStep (step) {
      // Bloqueia edição de steps de acompanhante
      if (step.is_companion_step === true || (step.name || '').startsWith('Acompanhante')) {
        this.$bvModal.msgBoxOk(
          'Steps de acompanhante não podem ser editados. Eles são gerados automaticamente baseados nas configurações do formulário.',
          {
            title: 'Step de Acompanhante',
            okVariant: 'info',
            okTitle: 'Entendi',
            centered: true
          }
        )
        return
      }

      this.editingStep = { ...step }
      this.showStepModal = true
    },

    saveStep () {
      if (!this.editingStep.name) {
        return
      }

      if (this.editingStep.id || this.editingStep._tempId) {
        const index = this.localSteps.findIndex(s => {
          const matchId = s.id && this.editingStep.id && String(s.id) === String(this.editingStep.id)
          const matchTempId = s._tempId && this.editingStep._tempId && String(s._tempId) === String(this.editingStep._tempId)
          return matchId || matchTempId
        })
        if (index !== -1) {
          this.$set(this.localSteps, index, { ...this.editingStep })
        }
      } else {
        const isFirstStep = this.localSteps.length === 0
        const newStep = {
          _tempId: this.generateTempId(),
          name: this.editingStep.name,
          description: this.editingStep.description,
          order: this.localSteps.length
        }
        console.log('➕ Criando novo step:', newStep.name, '_tempId:', newStep._tempId, 'isFirstStep:', isFirstStep)
        this.localSteps.push(newStep)

        // Se é o primeiro step, migrar campos sem step_id (campos standard)
        if (isFirstStep) {
          console.log('🔄 Primeiro step criado, migrando campos standard...')
          const camposSemStep = this.localFields.filter(f => f.step_id === null || f.step_id === undefined)
          console.log(`  📦 ${camposSemStep.length} campos sem step encontrados`)

          this.localFields.forEach(field => {
            if (field.step_id === null || field.step_id === undefined) {
              const oldStepId = field.step_id
              field.step_id = newStep._tempId
              console.log(`  ✅ Campo "${field.name}" migrado de step_id=${oldStepId} para step_id=${newStep._tempId}`)
            }
          })
        } else {
          console.log(`  ℹ️ Não é o primeiro step (${this.localSteps.length} steps no total), não migrando campos`)
        }

        // Selecionar automaticamente o step recém-criado
        this.selectedStep = newStep
        console.log('  ✅ Step selecionado automaticamente')
      }

      this.showStepModal = false
    },

    deleteStep (step) {
      // Bloqueia exclusão de steps de acompanhante
      if (step.is_companion_step === true || (step.name || '').startsWith('Acompanhante')) {
        this.$bvModal.msgBoxOk(
          'Steps de acompanhante não podem ser removidos. Para remover, desabilite a opção de acompanhantes nas configurações do formulário.',
          {
            title: 'Step de Acompanhante',
            okVariant: 'info',
            okTitle: 'Entendi',
            centered: true
          }
        )
        return
      }

      console.log('🗑️ Deletando step:', step.name, 'ID:', step.id, '_tempId:', step._tempId)
      console.log('📋 Array localSteps ANTES da deleção:')
      this.localSteps.forEach((s, idx) => {
        console.log(`  [${idx}] ${s.name} - id: ${s.id}, _tempId: ${s._tempId}`)
      })

      // Contar campos neste step
      const fieldsInStep = this.localFields.filter(f =>
        String(f.step_id) === String(step.id) || String(f.step_id) === String(step._tempId)
      )

      console.log('📦 Campos encontrados neste step:', fieldsInStep.length)
      fieldsInStep.forEach(f => console.log(`  - ${f.name} (step_id: ${f.step_id})`))

      // Separar campos standard e customizados
      const standardFields = fieldsInStep.filter(f =>
        f._isStandard === true ||
        (f.field_type && f.field_type.is_standard === true) ||
        (f.fieldType && f.fieldType.is_standard === true)
      )
      const customFields = fieldsInStep.filter(f =>
        !(f._isStandard === true ||
          (f.field_type && f.field_type.is_standard === true) ||
          (f.fieldType && f.fieldType.is_standard === true))
      )

      // Mensagem condicional
      let message = 'Deseja realmente excluir esta etapa?'
      if (fieldsInStep.length > 0) {
        const parts = []
        if (customFields.length > 0) {
          parts.push(`${customFields.length} campo(s) customizado(s) será(ão) deletado(s)`)
        }
        if (standardFields.length > 0) {
          parts.push(`${standardFields.length} campo(s) padrão será(ão) movido(s) para "Todos"`)
        }
        message = `Esta etapa contém ${fieldsInStep.length} campo(s). ${parts.join(' e ')}. Continuar?`
      }

      this.$bvModal.msgBoxConfirm(
        message,
        {
          title: 'Confirmar Exclusão',
          okVariant: 'danger',
          okTitle: 'Excluir',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(confirmed => {
        if (confirmed) {
          console.log('✅ Confirmado. Deletando step e processando campos...')
          console.log('🔍 Procurando step com id:', step.id, '_tempId:', step._tempId)

          // Processar campos deste step
          const fieldsToRemove = []
          this.localFields.forEach((f, index) => {
            if (String(f.step_id) === String(step.id) || String(f.step_id) === String(step._tempId)) {
              // Verificar se é campo standard
              const isStandard = f._isStandard === true ||
                                (f.field_type && f.field_type.is_standard === true) ||
                                (f.fieldType && f.fieldType.is_standard === true)

              if (isStandard) {
                // Campos standard: mover para "Todos os Campos" (não podem ser deletados)
                console.log(`  🔄 Campo standard "${f.name}" movido para "Todos os Campos"`)
                f.step_id = null
              } else {
                // Campos customizados: marcar para remoção
                console.log(`  🗑️ Campo customizado "${f.name}" será deletado`)
                fieldsToRemove.push(index)
              }
            }
          })

          // Remover campos customizados (em ordem reversa para não bagunçar os índices)
          fieldsToRemove.reverse().forEach(index => {
            const removedField = this.localFields[index]
            this.localFields.splice(index, 1)
            console.log(`  ✅ Campo "${removedField.name}" removido`)

            // Se o campo removido era o selecionado, limpar seleção
            if (this.selectedField && (this.selectedField.id === removedField.id || this.selectedField._tempId === removedField._tempId)) {
              this.selectedField = null
            }
          })

          // Remover o step
          console.log('🔍 Buscando index do step no array...')
          const index = this.localSteps.findIndex(s => {
            const matchId = s.id && step.id && String(s.id) === String(step.id)
            const matchTempId = s._tempId && step._tempId && String(s._tempId) === String(step._tempId)
            console.log(`  Comparando [${this.localSteps.indexOf(s)}] ${s.name}: id match=${matchId}, tempId match=${matchTempId}`)
            return matchId || matchTempId
          })
          console.log(`🎯 Index encontrado: ${index}`)

          if (index !== -1) {
            const stepToRemove = this.localSteps[index]
            console.log(`  ⚠️ Removendo step no index ${index}: ${stepToRemove.name} (id: ${stepToRemove.id}, _tempId: ${stepToRemove._tempId})`)
            this.localSteps.splice(index, 1)
            console.log(`  ✅ Step removido do array`)
          }

          // Se o step deletado era o selecionado, selecionar outro step disponível
          const selectedMatchesDeleted = this.selectedStep && (
            (this.selectedStep.id && step.id && String(this.selectedStep.id) === String(step.id)) ||
            (this.selectedStep._tempId && step._tempId && String(this.selectedStep._tempId) === String(step._tempId))
          )
          if (selectedMatchesDeleted) {
            console.log('  🔄 Step deletado era o selecionado, escolhendo outro step...')

            // Tentar selecionar o step anterior ao deletado, ou o primeiro disponível
            if (this.localSteps.length > 0) {
              const previousStep = this.localSteps[Math.max(0, index - 1)]
              this.selectedStep = previousStep
              console.log(`  ✅ Step "${previousStep.name}" selecionado automaticamente`)
            } else {
              // Não há mais steps, voltar para "Todos"
              this.selectedStep = null
              console.log('  ℹ️ Nenhum step restante, voltando para "Todos"')
            }
          }

          console.log('📊 Estado final:')
          console.log('  - Total de steps:', this.localSteps.length)
          console.log('  - Total de campos:', this.localFields.length)
          console.log('  - Campos por step:')
          this.localSteps.forEach(s => {
            const count = this.localFields.filter(f =>
              String(f.step_id) === String(s.id) || String(f.step_id) === String(s._tempId)
            ).length
            console.log(`    - ${s.name}: ${count} campos`)
          })
          const noStep = this.localFields.filter(f => f.step_id === null || f.step_id === undefined).length
          console.log(`    - Sem step: ${noStep} campos`)
        }
      })
    },

    onStepsReorder () {
      this.localSteps = this.localSteps.map((step, index) => ({
        ...step,
        order: index
      }))
    },

    // Form Actions
    async saveForm () {
      // Guard de re-entrada: impede saves concorrentes
      if (this.saving) {
        console.warn('⚠️ Save já em andamento, ignorando chamada duplicada')
        return
      }

      if (!this.formData.name) {
        errorMessage('Digite o nome do formulário')
        return
      }

      this.saving = true
      try {
        // Create a mapping of _tempId to step reference for new steps
        const stepRefMap = {}
        const steps = this.localSteps.map((s, index) => {
          const stepData = {
            ...s,
            order: index,
            id: s.id || undefined
          }
          // If step has _tempId but no id, create a reference
          if (s._tempId && !s.id) {
            stepData._ref = s._tempId
            stepRefMap[s._tempId] = s._tempId
          }
          return stepData
        })

        // Map fields with proper step references
        const fields = this.localFields.map((f, index) => {
          const fieldData = {
            ...f,
            order: index,
            id: f.id || undefined,
            field_type_id: f.field_type_id || f.fieldType?.id || (f.fieldType?.name ? this.getFieldTypeIdByName(f.fieldType.name) : undefined)
          }

          // Handle step_id references
          if (f.step_id) {
            // Check if step_id is a _tempId (new step not yet saved)
            if (stepRefMap[f.step_id]) {
              // Send as _step_ref so backend can match it
              fieldData._step_ref = f.step_id
              fieldData.step_id = undefined
            } else {
              // It's a real step_id from database
              fieldData.step_id = f.step_id
            }
          }

          return fieldData
        })

        if (!this.isEditing) {
          // Para criar: dados no root
          // IMPORTANTE: Filtrar campos standard pois o backend já cria automaticamente
          const customFieldsOnly = fields.filter(f => {
            const isStandard = f._isStandard === true ||
                             (f.field_type && f.field_type.is_standard === true) ||
                             (f.fieldType && f.fieldType.is_standard === true)
            return !isStandard
          })

          console.log(`📤 Enviando ${customFieldsOnly.length} campos customizados (${fields.length - customFieldsOnly.length} standard filtrados)`)

          const payload = {
            ...this.formData,
            settings: this.formSettings,
            fields: customFieldsOnly, // Enviar apenas campos customizados
            steps
          }
          const form = await this.CreateForm(payload)
          this.$router.replace({
            name: 'form-builder-edit',
            params: { id: form.id }
          })
          successMessage('Formulário criado com sucesso!')
        } else {
          // Para editar: dados dentro de "form"
          const formData = {
            ...this.formData,
            settings: this.formSettings
          }

          console.log('📤 Salvando formulário com status:', formData.status)

          // Commit form, steps e fields para o store antes de salvar
          this.$store.commit('formBuilder/SET_FORM', formData)
          this.$store.commit('formBuilder/SET_STEPS', steps)
          this.$store.commit('formBuilder/SET_FIELDS', fields)
          await this.SaveFormComplete()
          // Recarregar o formulário para obter os IDs corretos do backend
          await this.loadForm(this.formData.id)
          successMessage('Formulário salvo com sucesso!')
        }
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao salvar: ' + (err.response?.data?.message || err.message))
      } finally {
        this.saving = false
      }
    },

    async saveSettings () {
      console.log('🔍 Salvando configurações. Status:', this.formData.status, 'Tipo:', typeof this.formData.status)

      // v-model.number já converte para número
      // Apenas garantir que seja 1 ou 0
      this.formData.status = this.formData.status ? 1 : 0
      this.formData.link_companion = this.formData.link_companion ? 1 : 0

      // Salva companion_config
      if (this.companionConfig.enabled) {
        this.formData.companion_config = {
          enabled: this.companionConfig.enabled,
          mode: this.companionConfig.mode,
          max_companions: this.companionConfig.max_companions,
          companion_form_id: this.companionConfig.companion_form_id,
          email_template_id: this.companionConfig.email_template_id
        }
      } else {
        this.formData.companion_config = null
      }

      // Sincroniza steps de acompanhante
      this.syncCompanionSteps()

      this.showSettingsModal = false

      // Se está editando um formulário existente, salva imediatamente
      if (this.isEditing) {
        await this.saveForm()
      } else {
        // Se é novo formulário, apenas fecha o modal
        // O usuário precisa clicar em "Salvar" no topo para criar
        successMessage('Configurações salvas. Clique em "Salvar" no topo para criar o formulário.')
      }
    },

    syncCompanionSteps () {
      // Se acompanhante não está habilitado, remove tudo
      if (!this.companionConfig.enabled) {
        this.localSteps = this.localSteps.filter(step => {
          const isCompanion = step.is_companion_step === true || (step.name || '').startsWith('Acompanhante')
          return !isCompanion
        })
        this.localFields = this.localFields.filter(field => {
          return !(field.name || '').startsWith('companion_') && field.is_companion_field !== true
        })
        return
      }

      const maxCompanions = this.companionConfig.max_companions || 1
      const mode = this.companionConfig.mode || 'invitation'

      // Verifica companion steps já salvos no banco (têm ID real)
      const existingCompanionSteps = this.localSteps.filter(s =>
        (s.is_companion_step === true || (s.name || '').startsWith('Acompanhante')) && s.id
      )

      if (existingCompanionSteps.length > 0) {
        // Companion steps já existem no banco — preservar tudo (steps + campos)
        // Apenas remover excedentes se max_companions diminuiu
        if (existingCompanionSteps.length > maxCompanions) {
          const toRemove = existingCompanionSteps.slice(maxCompanions)
          const removeStepIds = toRemove.map(s => s.id)
          this.localSteps = this.localSteps.filter(s => !removeStepIds.includes(s.id))
          this.localFields = this.localFields.filter(f =>
            !removeStepIds.includes(f.step_id)
          )
        }

        // Criar novos companion steps se max_companions aumentou
        for (let i = existingCompanionSteps.length + 1; i <= maxCompanions; i++) {
          const stepName = maxCompanions === 1 ? 'Acompanhante' : `Acompanhante ${i}`
          const stepTempId = `companion_step_${i}_${Date.now()}_${Math.random()}`
          const companionStep = {
            _tempId: stepTempId,
            name: stepName,
            description: mode === 'invitation'
              ? 'Dados básicos do acompanhante (receberá convite por email)'
              : 'Dados completos do acompanhante',
            order: this.localSteps.length,
            is_companion_step: true,
            companion_index: i
          }
          this.localSteps.push(companionStep)
          this.createCompanionFields(companionStep, i, mode)
        }

        console.log('✅ Steps de acompanhante preservados do banco:', existingCompanionSteps.length)
        return
      }

      // Nenhum companion step no banco — remover temporários e criar do zero
      this.localSteps = this.localSteps.filter(step => {
        const isCompanion = step.is_companion_step === true || (step.name || '').startsWith('Acompanhante')
        return !isCompanion
      })
      this.localFields = this.localFields.filter(field => {
        return !(field.name || '').startsWith('companion_') && field.is_companion_field !== true
      })

      // Cria steps de acompanhante
      for (let i = 1; i <= maxCompanions; i++) {
        const stepName = maxCompanions === 1 ? 'Acompanhante' : `Acompanhante ${i}`
        const stepTempId = `companion_step_${i}_${Date.now()}_${Math.random()}`

        const companionStep = {
          _tempId: stepTempId,
          name: stepName,
          description: mode === 'invitation'
            ? 'Dados básicos do acompanhante (receberá convite por email)'
            : 'Dados completos do acompanhante',
          order: this.localSteps.length,
          is_companion_step: true,
          companion_index: i
        }

        this.localSteps.push(companionStep)
        this.createCompanionFields(companionStep, i, mode)
      }

      console.log('✅ Steps de acompanhante criados:', this.localSteps.filter(s => s.is_companion_step).length)
    },

    createCompanionFields (step, companionIndex, mode) {
      const prefix = `companion_${companionIndex}_`
      const stepId = step._tempId

      // Campo: Levarei acompanhante?
      const hasCompanionField = {
        _tempId: `${prefix}has_companion_${Date.now()}_${Math.random()}`,
        name: `${prefix}has_companion`,
        label: 'Levarei acompanhante?',
        field_type_id: this.getFieldTypeIdByName('select'),
        fieldType: { name: 'select' },
        step_id: stepId,
        required: true,
        order: 0,
        layout: { cols: '12' },
        config: { type: 'select' },
        options: [
          { label: 'Sim', value: 'Sim' },
          { label: 'Não', value: 'Não' }
        ],
        is_companion_field: true
      }

      // Campo: Nome (condicional)
      const nameField = {
        _tempId: `${prefix}companion_name_${Date.now()}_${Math.random()}`,
        name: `${prefix}companion_name`,
        label: 'Nome do acompanhante',
        field_type_id: this.getFieldTypeIdByName('text'),
        fieldType: { name: 'text' },
        step_id: stepId,
        required: true,
        order: 1,
        layout: { cols: '12' },
        config: { type: 'text' },
        placeholder: 'Nome completo',
        is_companion_field: true,
        conditions: [{
          logic: 'and',
          rules: [{
            field_id: hasCompanionField.id || hasCompanionField._tempId,
            operator: '==',
            value: 'Sim'
          }]
        }]
      }

      // Campo: Email (condicional)
      const emailField = {
        _tempId: `${prefix}companion_email_${Date.now()}_${Math.random()}`,
        name: `${prefix}companion_email`,
        label: 'E-mail do acompanhante',
        field_type_id: this.getFieldTypeIdByName('email'),
        fieldType: { name: 'email' },
        step_id: stepId,
        required: true,
        order: 2,
        layout: { cols: '12' },
        config: { type: 'email' },
        placeholder: 'email@exemplo.com',
        is_companion_field: true,
        conditions: [{
          logic: 'and',
          rules: [{
            field_id: hasCompanionField.id || hasCompanionField._tempId,
            operator: '==',
            value: 'Sim'
          }]
        }]
      }

      // Campo: Telefone (condicional)
      const phoneField = {
        _tempId: `${prefix}companion_phone_${Date.now()}_${Math.random()}`,
        name: `${prefix}companion_phone`,
        label: 'Telefone do acompanhante',
        field_type_id: this.getFieldTypeIdByName('phone'),
        fieldType: { name: 'phone' },
        step_id: stepId,
        required: false,
        order: 3,
        layout: { cols: '12' },
        config: { type: 'phone' },
        placeholder: '(00) 00000-0000',
        mask: '(##) #####-####',
        is_companion_field: true,
        conditions: [{
          logic: 'and',
          rules: [{
            field_id: hasCompanionField.id || hasCompanionField._tempId,
            operator: '==',
            value: 'Sim'
          }]
        }]
      }

      // Adiciona campos à lista
      this.localFields.push(hasCompanionField, nameField, emailField, phoneField)

      console.log(`✅ Campos criados para ${step.name}:`, [
        hasCompanionField.name,
        nameField.name,
        emailField.name,
        phoneField.name
      ])
    },

    getFieldTypeIdByName (typeName) {
      // Busca o ID do field_type pelo nome, usando os tipos carregados da API
      if (this.fieldTypesGrouped && typeof this.fieldTypesGrouped === 'object') {
        const groups = Array.isArray(this.fieldTypesGrouped)
          ? this.fieldTypesGrouped
          : Object.values(this.fieldTypesGrouped).flat()
        for (const group of groups) {
          // Estrutura: { category, types: [{ id, name, ... }] }
          if (group && Array.isArray(group.types)) {
            for (const t of group.types) {
              if (t && t.name === typeName && t.id) return t.id
            }
          }
          // Fallback: caso seja flat (objeto direto com id/name)
          if (group && group.name === typeName && group.id) return group.id
        }
      }
      // Sem fallback numérico: os IDs variam por instalação e chutar aqui grava o
      // campo com o tipo errado. Quem chama envia fieldType.name e o backend resolve.
      console.warn(`[form-builder] field_type "${typeName}" não encontrado nos tipos carregados`)
      return null
    },

    previewForm () {
      this.previewCurrentStep = 0
      this.showPreview = true
    },

    goBack () {
      // Formulário de coleta de lead é gerenciado dentro do Controle de Acesso
      if (this.formData.type === 'checkin_lead' || this.$route.path.indexOf('/settings/checkin') === 0) {
        this.$router.push('/settings/checkin')
      } else {
        this.$router.push({ name: 'form-builder' })
      }
    },

    // Preview helpers
    getPreviewStepFields (stepIndex) {
      if (!this.localSteps[stepIndex]) return []
      const step = this.localSteps[stepIndex]
      return this.localFields.filter(f =>
        String(f.step_id) === String(step.id) || String(f.step_id) === String(step._tempId)
      )
    },

    getFieldWidthClass (field) {
      const cols = field.layout?.cols || '12'
      return {
        'col-12': cols === '12',
        'col-6': cols === '6',
        'col-4': cols === '4',
        'col-3': cols === '3'
      }
    },

    getPreviewFieldComponent (field) {
      const typeName = field.fieldType?.name || field.field_type?.name || 'text'

      // Return inline functional component based on type
      const textTypes = ['text', 'email', 'phone', 'cpf', 'cnpj', 'cep', 'number', 'password', 'rg']
      const selectTypes = ['select', 'multiselect', 'state']

      if (typeName === 'textarea') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('textarea', {
            class: 'form-control',
            attrs: { placeholder: ctx.props.field.placeholder, rows: 3, disabled: true }
          })
        }
      }

      if (selectTypes.includes(typeName)) {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('select', {
            class: 'form-control',
            attrs: { disabled: true }
          }, [
            h('option', ctx.props.field.placeholder || 'Selecione...'),
            ...(ctx.props.field.options || []).map(opt =>
              h('option', { attrs: { value: opt.value } }, opt.label)
            )
          ])
        }
      }

      if (typeName === 'radio' || typeName === 'checkbox') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('div', { class: 'options-preview' },
            (ctx.props.field.options || []).map(opt =>
              h('div', { class: 'form-check' }, [
                h('input', {
                  class: 'form-check-input',
                  attrs: { type: typeName, disabled: true }
                }),
                h('label', { class: 'form-check-label' }, opt.label)
              ])
            )
          )
        }
      }

      if (typeName === 'file' || typeName === 'image') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('div', { class: 'file-preview' }, [
            h('i', { class: typeName === 'image' ? 'bx bx-image' : 'bx bx-upload' }),
            h('span', 'Clique para selecionar ou arraste um arquivo')
          ])
        }
      }

      if (typeName === 'date' || typeName === 'datetime' || typeName === 'birthdate') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('input', {
            class: 'form-control',
            attrs: { type: 'date', disabled: true }
          })
        }
      }

      if (typeName === 'time') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('input', {
            class: 'form-control',
            attrs: { type: 'time', disabled: true }
          })
        }
      }

      if (typeName === 'toggle' || typeName === 'terms') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('div', { class: 'custom-control custom-switch' }, [
            h('input', {
              class: 'custom-control-input',
              attrs: { type: 'checkbox', disabled: true }
            }),
            h('label', { class: 'custom-control-label' }, '')
          ])
        }
      }

      // Default: text input
      if (textTypes.includes(typeName) || typeName === 'hidden') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) => h('input', {
            class: 'form-control',
            attrs: {
              type: typeName === 'password' ? 'password' : 'text',
              placeholder: ctx.props.field.placeholder,
              disabled: true
            }
          })
        }
      }

      // Fallback
      return {
        functional: true,
        props: ['field'],
        render: (h, ctx) => h('input', {
          class: 'form-control',
          attrs: { type: 'text', placeholder: ctx.props.field.placeholder, disabled: true }
        })
      }
    },

    isTextField (field) {
      const textTypes = ['text', 'email', 'phone', 'cpf', 'cnpj', 'cep', 'number', 'password', 'hidden', 'rg']
      return textTypes.includes(field.fieldType?.name)
    },

    isTextArea (field) {
      return field.fieldType?.name === 'textarea'
    },

    isSelect (field) {
      const selectTypes = ['select', 'multiselect', 'state']
      return selectTypes.includes(field.fieldType?.name)
    }
  }
}
</script>

<style scoped>
.form-builder {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
}

/* Loading Overlay */
.builder-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 246, 250, 0.82);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.builder-loading-content {
  text-align: center;
  background: #fff;
  padding: 32px 48px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.builder-loading-content p {
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 500;
  color: #495057;
}

.builder-loading-spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto;
  border: 3px solid #e9ecef;
  border-top-color: #556ee6;
  border-radius: 50%;
  animation: builder-spin 0.8s linear infinite;
}

@keyframes builder-spin {
  to { transform: rotate(360deg); }
}

.fade-overlay-enter-active {
  transition: opacity 0.15s ease;
}
.fade-overlay-leave-active {
  transition: opacity 0.3s ease;
}
.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}

/* Header */
.builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.form-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-title-input {
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
  padding: 4px 0;
  width: 300px;
}

.form-title-input:focus {
  outline: none;
  border-bottom: 2px solid #556ee6;
}

.form-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.form-status.active {
  background: #d1fae5;
  color: #059669;
}

.form-status.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-settings,
.btn-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-settings:hover,
.btn-preview:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save:hover:not(:disabled) {
  background: #485ec4;
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Steps Bar */
.steps-bar {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 0 20px;
  flex-shrink: 0;
}

.steps-container {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  padding: 8px 0;
}

.steps-draggable {
  display: flex;
  gap: 4px;
}

.step-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.step-tab:hover {
  border-color: #d0d5dd;
  background: #f0f2f5;
}

.step-tab.active {
  background: #556ee6;
  border-color: #556ee6;
  color: #fff;
}

.step-tab.companion-step {
  background: #e7f3ff;
  border-color: #b3d9ff;
}

.step-tab.companion-step:hover {
  background: #d6ebff;
  border-color: #99ccff;
}

.step-tab.companion-step.active {
  background: #17a2b8;
  border-color: #17a2b8;
  color: #fff;
}

.step-drag-handle {
  cursor: grab;
  color: #adb5bd;
  display: flex;
}

.step-tab.active .step-drag-handle {
  color: rgba(255, 255, 255, 0.7);
}

.step-icon-all {
  display: flex;
  align-items: center;
  color: #556ee6;
}

.step-tab.active .step-icon-all {
  color: #fff;
}

.step-number {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
}

.step-tab.active .step-number {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.step-name {
  font-size: 13px;
  font-weight: 500;
}

.step-count {
  font-size: 11px;
  padding: 2px 6px;
  background: #e9ecef;
  border-radius: 10px;
  color: #6c757d;
}

.step-tab.active .step-count {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.step-edit-btn,
.step-delete-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  color: inherit;
}

.step-tab:hover .step-edit-btn,
.step-tab:hover .step-delete-btn {
  opacity: 0.7;
}

.step-edit-btn:hover,
.step-delete-btn:hover {
  opacity: 1 !important;
}

.btn-add-step {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px dashed #adb5bd;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  color: #6c757d;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.btn-add-step:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Main Content */
.builder-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-left {
  width: 320px;
  border-right: 1px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
}

.panel-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f6fa;
}

.canvas-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.canvas-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.canvas-header h5 i {
  color: #556ee6;
}

.canvas-header .field-count {
  font-size: 12px;
  color: #6c757d;
}

.canvas-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.canvas-scroll::-webkit-scrollbar {
  width: 6px;
}

.canvas-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.canvas-scroll::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.canvas-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.panel-right {
  width: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  transition: width 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.panel-right.has-selection {
  width: 360px;
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.no-selection-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}

.no-selection-icon i {
  font-size: 28px;
  color: #556ee6;
}

.no-selection h5 {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.no-selection p {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* Settings Form */
.settings-form .form-group {
  margin-bottom: 16px;
}

.settings-form label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  display: block;
}

.settings-form h6 {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 16px;
}

/* Preview Modal */
.form-preview {
  padding: 0;
}

.preview-header {
  text-align: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e9ecef;
}

.preview-header h4 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #344054;
}

.preview-header p {
  margin: 0;
  font-size: 14px;
  color: #6c757d;
}

/* Preview Steps */
.preview-steps {
  display: flex;
  flex-direction: column;
}

.preview-steps-bar {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 20px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
}

.preview-step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.15s;
}

.preview-step-item:hover {
  border-color: #556ee6;
}

.preview-step-item.active {
  background: #556ee6;
  border-color: #556ee6;
  color: #fff;
}

.preview-step-item.completed {
  background: #d1fae5;
  border-color: #10b981;
  color: #059669;
}

.preview-step-number {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
}

.preview-step-item.active .preview-step-number {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.preview-step-item.completed .preview-step-number {
  background: #10b981;
  color: #fff;
}

.preview-step-name {
  font-size: 13px;
  font-weight: 500;
}

.preview-step-content {
  padding: 24px;
}

.preview-step-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
}

.preview-step-desc {
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #6c757d;
}

/* Preview Fields Grid */
.preview-fields-grid {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -12px;
}

.preview-field {
  padding: 0 12px;
  margin-bottom: 20px;
}

.preview-field.col-12 { width: 100%; }
.preview-field.col-6 { width: 50%; }
.preview-field.col-4 { width: 33.333%; }
.preview-field.col-3 { width: 25%; }

.preview-field label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
}

.preview-field .required-mark {
  color: #dc2626;
}

.preview-field .help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.preview-field .form-control {
  font-size: 14px;
}

.preview-field .file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
  color: #6c757d;
  font-size: 13px;
}

.preview-field .file-preview i {
  font-size: 32px;
  margin-bottom: 8px;
  color: #adb5bd;
}

.preview-field .options-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Preview Navigation */
.preview-step-nav {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.preview-single-page {
  padding: 24px;
}

.preview-submit {
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #9ca3af;
}

.empty-preview i {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-preview p {
  margin: 0;
  font-size: 14px;
}

/* Responsive Preview */
@media (max-width: 768px) {
  .preview-field.col-6,
  .preview-field.col-4,
  .preview-field.col-3 {
    width: 100%;
  }
}

/* Responsive */
@media (max-width: 1400px) {
  .panel-left {
    width: 280px;
  }
  .panel-right.has-selection {
    width: 320px;
  }
}

@media (max-width: 1200px) {
  .panel-right.has-selection {
    width: 300px;
  }
}
</style>

<style>
/* Global style to hide footer on form builder page */
body.form-builder-page {
  overflow: hidden !important;
  height: 100vh !important;
}

body.form-builder-page footer.footer {
  display: none !important;
}

body.form-builder-page .main-content {
  padding: 0 !important;
  /* Keep margin-left for sidebar */
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  overflow: hidden !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 70px !important;
  overflow: hidden !important;
  min-height: 0 !important;
  height: calc(100vh - 70px) !important;
  max-height: calc(100vh - 70px) !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content > .container-fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
</style>
